<template>
  <div>
    <dialog-v2
      :show="show"
      :title="dialogTitle"
      max-width="800px"
      @close="closeDialog"
      @save="save"
    >
      <template slot="content">
        <v-tabs v-model="activeTab" class="campaign-tabs" grow>
          <v-tabs-slider class="campaign-tabs__slider" />
          <v-tab ripple class="campaign-tab">
            <v-icon small class="mr-2">fas fa-info-circle</v-icon>
            Información general
            <v-icon v-if="errors.any('callList')" small class="tab-error-icon ml-1">fas fa-exclamation-circle</v-icon>
          </v-tab>
          <v-tab ripple class="campaign-tab">
            <v-icon small class="mr-2">fas fa-filter</v-icon>
            Filtros
          </v-tab>
          <v-tab ripple class="campaign-tab">
            <v-icon small class="mr-2">fas fa-users</v-icon>
            Priorización de clientes
          </v-tab>

          <!-- Tab 1: Información general -->
          <v-tab-item class="tab-content">
            <v-layout row wrap>
              <v-flex md4 pr-3>
                <v-text-field
                  v-model="form.name"
                  v-validate="'required|max:50'"
                  data-vv-name="name"
                  data-vv-scope="callList"
                  data-vv-as="nombre"
                  color="black"
                  label="Nombre"
                  :error-messages="errors.collect('callList.name')"
                />
              </v-flex>
              <v-flex md4 pr-3>
                <v-text-field
                  v-model="form.descrip"
                  v-validate="'max:100'"
                  data-vv-name="descrip"
                  data-vv-scope="callList"
                  data-vv-as="descripción"
                  color="black"
                  label="Descripción"
                  :error-messages="errors.collect('callList.descrip')"
                />
              </v-flex>
              <v-flex md4 pr-3>
                <v-select
                  v-model="form.type_id"
                  :items="types"
                  item-value="id"
                  item-text="label"
                  v-validate="editMode ? null : 'required'"
                  :disabled="editMode"
                  data-vv-scope="callList"
                  data-vv-name="type_id"
                  data-vv-as="Tipo"
                  :error-messages="errors.collect('callList.type_id')"
                  label="Tipo"
                  color="black"
                  autocomplete
                />
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex md4 pr-3>
                <v-menu
                  ref="startDatepickerRef"
                  :close-on-content-click="false"
                  v-model="startDatePicker"
                  :nudge-right="40"
                  :return-value.sync="form.start_date"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px"
                >
                  <v-text-field
                    slot="activator"
                    :value="formatDate(form.start_date, 'YYYY-MM-DD', 'DD/MM/YYYY')"
                    prepend-icon="event"
                    color="black"
                    readonly
                  />
                  <v-date-picker
                    v-model="form.start_date"
                    @input="$refs.startDatepickerRef.save(form.start_date)"
                    color="k-orange-linear"
                  />
                </v-menu>
              </v-flex>
              <v-flex md4 pr-3>
                <v-menu
                  ref="endDatepickerRef"
                  :close-on-content-click="false"
                  v-model="endDatePicker"
                  :nudge-right="40"
                  :return-value.sync="form.end_date"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px"
                >
                  <v-text-field
                    slot="activator"
                    :value="formatDate(form.end_date, 'YYYY-MM-DD', 'DD/MM/YYYY')"
                    prepend-icon="event"
                    color="black"
                    readonly
                  />
                  <v-date-picker
                    v-model="form.end_date"
                    @input="$refs.endDatepickerRef.save(form.end_date)"
                    color="k-orange-linear"
                  />
                </v-menu>
              </v-flex>
              <v-flex md4 pr-3>
                <v-text-field
                  v-if="form.type_id !== 10"
                  v-model="form.extensions"
                  v-validate="'required|numeric'"
                  data-vv-name="extensions"
                  data-vv-scope="callList"
                  data-vv-as="Extensiones por operador"
                  color="black"
                  label="Extensiones por operador"
                  :error-messages="errors.collect('callList.extensions')"
                />
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex md4 pr-3>
                <v-text-field
                  :disabled="!form.type_id"
                  v-model.number="form.max_commitment_days"
                  v-validate="'required|numeric'"
                  data-vv-name="max_commitment_days"
                  data-vv-scope="callList"
                  data-vv-as="maximo de compromiso"
                  color="black"
                  label="Maximo de compromiso"
                  :error-messages="errors.collect('callList.max_commitment_days')"
                />
              </v-flex>
              <v-flex md4>
                <v-text-field
                  v-if="form.type_id !== 10"
                  v-model.number="form.priority"
                  v-validate="'required|numeric'"
                  data-vv-name="priority"
                  data-vv-scope="callList"
                  data-vv-as="prioridad"
                  color="black"
                  label="Prioridad"
                  min="1"
                  :error-messages="errors.collect('callList.priority')"
                />
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex md4 pr-3>
                <v-text-field
                  :disabled="editMode || !$auth.user().permissions.includes('can_edit_max_attempts')"
                  v-model="form.max_attempts_current"
                  v-validate="'required|numeric'"
                  data-vv-name="max_attempts_current"
                  data-vv-scope="callList"
                  data-vv-as="Intentos máximos"
                  color="black"
                  min="1"
                  :label="!editMode ? 'Intentos máximos' : 'Intentos actuales'"
                  :error-messages="errors.collect('callList.max_attempts_current')"
                />
              </v-flex>
              <v-flex md4 pr-3 v-if="editMode && $auth.user().permissions.includes('can_edit_max_attempts')" :class="!isInvalid ? 'attempt-container' : ''">
                <v-text-field
                  v-model="form.max_attempts_new"
                  v-validate="checkValidate"
                  data-vv-name="max_attempts_new"
                  data-vv-scope="callList"
                  data-vv-as="Nuevos Intentos"
                  color="black"
                  min="1"
                  label="Nuevos intentos máximos"
                  :error-messages="errors.collect('callList.max_attempts_new')"
                />
                <p class="message">Los cambios en intentos maximos se veran reflejados en la próxima carga</p>
              </v-flex>
              <v-flex md4>
                <v-text-field
                  :disabled="!$auth.user().permissions.includes('can_edit_max_extensions')"
                  v-model="form.max_extensions"
                  v-validate="'required|numeric'"
                  data-vv-name="max_extensions"
                  data-vv-scope="callList"
                  data-vv-as="Extensiones máximas"
                  color="black"
                  min="1"
                  label="Extensiones máximas"
                  :error-messages="errors.collect('callList.max_extensions')"
                />
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex row wrap :md8="[6, 7].includes(form.type_id)" pr-3 v-if="form.type_id !== 10">
                <v-select
                  v-model="form.companyLoads"
                  :items="companyLoad"
                  item-value="id"
                  item-text="name"
                  :disabled="editMode"
                  :multiple="true"
                  data-vv-scope="callList"
                  data-vv-name="companyLoads"
                  data-vv-as="Cargas a las que pertenece"
                  :error-messages="errors.collect('callList.companyLoads')"
                  label="Tipo de carga"
                  color="black"
                />
              </v-flex>
              <v-flex md4 v-if="[6, 7].includes(form.type_id)" pr-3>
                <v-text-field
                  :disabled="!form.type_id"
                  v-model.number="form.commitment_days_off"
                  v-validate="'required|numeric'"
                  data-vv-name="commitment_days_off"
                  data-vv-scope="callList"
                  data-vv-as="Dias antes del compromiso"
                  color="black"
                  label="Dias antes del compromiso"
                  :error-messages="errors.collect('callList.commitment_days_off')"
                />
              </v-flex>
              <v-flex md4 v-if="[8, 9].includes(form.type_id)" pr-6>
                <v-layout row wrap>
                  <v-flex xs12>
                    <input
                      class="input-file"
                      type="file"
                      ref="file"
                      @change="handleFileUpload($event)"
                      id="file-input"
                    />
                  </v-flex>
                </v-layout>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex>
                <v-select
                  clearable
                  v-model="form.survey_id"
                  :items="surveys"
                  item-value="id"
                  item-text="name"
                  data-vv-scope="callList"
                  data-vv-name="survey"
                  data-vv-as="encuesta de satisfacción"
                  :error-messages="errors.collect('callList.survey_id')"
                  label="Encuesta de satisfacción"
                  color="black"
                />
              </v-flex>
            </v-layout>
            <v-layout row wrap v-if="![8,9,10].includes(form.type_id)">
              <v-flex>
                <v-switch
                  label="Excluir KPI 8"
                  v-model="form.avoid_last_kpi"
                  class="k-blue-linear-text"
                  hint="Los clientes que pertenezcan al KPI 8 seran excluidos de la gestion"
                  :persistent-hint="true"
                />
              </v-flex>
            </v-layout>
            <v-layout v-else row wrap>
              <v-flex>
                <v-switch
                  v-if="form.type_id !== 10"
                  label="Respetar configuración de ley"
                  v-model="form.keep_law_config"
                  class="k-blue-linear-text"
                  hint="Indique si la campaña debe respetar la configuración de cobranza"
                  :persistent-hint="false"
                />
              </v-flex>
            </v-layout>
          </v-tab-item>

          <!-- Tab 2: Filtros -->
          <v-tab-item class="tab-content">
            <template v-if="form.type_id !== 10">
              <filters-list
                v-model="form.filters"
                :default-filters="form.filters"
                :showBtn="!editMode"
                :key="filtersListKey"
              />
            </template>
            <v-layout v-else row wrap mt-4 justify-center>
              <v-flex xs12 class="text-xs-center grey--text">
                <v-icon class="mr-2 grey--text">fas fa-info-circle</v-icon>
                Los filtros no están disponibles para campañas Inbound
              </v-flex>
            </v-layout>
          </v-tab-item>

          <!-- Tab 3: Grupos de clientes -->
          <v-tab-item class="tab-content">
            <template v-if="form.type_id !== 10">
              <customer-groups
                v-model="form.groups"
                :key="'groups-' + filtersListKey"
              />
            </template>
            <v-layout v-else row wrap mt-4 justify-center>
              <v-flex xs12 class="text-xs-center grey--text">
                <v-icon class="mr-2 grey--text">fas fa-info-circle</v-icon>
                Los grupos de clientes no están disponibles para campañas Inbound
              </v-flex>
            </v-layout>
          </v-tab-item>
        </v-tabs>
      </template>
    </dialog-v2>
    <!-- <groups-dialog
      :show="filtersDialog"
      :group="groupToEdit"
      @new-group="newGroup"
      @group-edited="groupEdited"
      @close="closeFiltersDialog"/> -->

    <v-dialog v-model="showGroupsWarning" max-width="480px" persistent>
      <v-card class="pa-2">
        <v-card-title class="pb-2">
          <v-icon class="mr-2" style="color:#f4a200">fas fa-exclamation-triangle</v-icon>
          <span class="title">Aviso importante</span>
        </v-card-title>
        <v-card-text class="pt-0 groups-warning-text">
          Los cambios realizados en la priorización de los clientes se aplicarán
          automáticamente en un periodo menor a 10 minutos.
        </v-card-text>
        <v-card-actions class="pt-0">
          <v-spacer />
          <v-btn flat color="grey" @click="showGroupsWarning = false">Cancelar</v-btn>
          <v-btn flat class="k-orange-linear-text font-weight-bold" @click="confirmGroupsWarning">
            Entendido, continuar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import moment from 'moment';
import _ from 'lodash';
import Scrollbar from 'vue-perfect-scrollbar';
import DialogV2 from '@/components/global/DialogV2';
// import GroupsDialog from '@/components/CallList/Modal/GroupsDialog';
import FiltersList from '@/components/global/CustomerFilters/Index';
import CustomerGroups from '@/components/CallList/Modal/CustomerGroups';
// import EmailTemplate from '@/components/Templates/Email'
import filtersMixin from '@/mixins/workflowFilters';
import formatDateMixin from '@/mixins/formatDate';
import comparisonOperatorsMixin from '@/mixins/comparisonOperators';
import EventBus from '@/event-bus';

const uuidv1 = require('uuid/v1');

export default {
  inject: ['$validator'],
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    callList: {
      type: Object,
      default() {
        return {};
      },
    },
    callsListsPriority: {
      type: Number,
      default: 1,
    },
  },
  components: {
    DialogV2,
    // GroupsDialog,
    Scrollbar,
    FiltersList,
    CustomerGroups,
    // EmailTemplate,
  },
  mixins: [filtersMixin, comparisonOperatorsMixin, formatDateMixin],
  data() {
    const now = moment();
    const emptyForm = {
      name: '',
      survey_id: null,
      descrip: '',
      // min_calls: 50,
      extensions: 5,
      start_date: now.startOf('isoWeek').format('YYYY-MM-DD'),
      end_date: now.endOf('isoWeek').format('YYYY-MM-DD'),
      avoid_last_kpi: true,
      keep_law_config: true,
      max_commitment_days: 3,
      commitment_days_off: 0,
      companyLoads: [],
      file: '',
      max_attempts_current: 15,
      max_attempts_new: null,
      max_extensions: 125,
      // send_email: false,
      // company_email_id: '',
      // email_subject: '',
      // email_text: '',
      priority: 1,
      filters: {
        type: 'group',
        operator: 'and',
        items: [],
      },
      groups: [],
      type_id: null,
      script_type_id: '',
    };
    return {
      activeTab: 0,
      showGroupsWarning: false,
      originalGroupsSnapshot: null,
      surveys: [],
      showCreate: false,
      filtersListKey: uuidv1(),
      // indexGroupToEdit: null,
      // filtersDialog: false,
      emptyForm,
      endDatePicker: false,
      startDatePicker: false,
      form: {
        ...emptyForm,
      },
      isInvalid: false,
      types: [
        {
          id: 1,
          label: 'Gestionando',
        },
        {
          id: 2,
          label: 'Informativo',
        },
        {
          id: 4,
          label: 'Acepta y rechaza oferta',
        },
        {
          id: 5,
          label: 'Acepta y rechaza oferta Informativo',
        },
        {
          id: 6,
          label: 'Compromiso',
        },
        {
          id: 7,
          label: 'Compromiso Informativo',
        },
        {
          id: 8,
          label: 'Sin inteligencia',
        },
        {
          id: 9,
          label: 'Telefono directo',
        },
        {
          id: 10,
          label: 'Inbound',
        },
      ],
      // emailTemplateForm: {
      //   company_email_id: '',
      //   subject: '',
      //   text: '',
      // },
      // selectedTab: '0',
    };
  },
  created() {
    Vue.axios.get('/surveys?paginate=0').then((response) => {
      this.surveys = response.data.filter(s => !s.manual && !s.inbound);
    });
  },
  computed: {
    // ...mapGetters([
    //   'customerStatuses',
    // ]),
    ...mapGetters([
      'companyLoad',
    ]),
    editMode() {
      return Object.keys(this.callList).length > 0;
    },
    dialogTitle() {
      let title = '';
      if (this.editMode) {
        title = 'Detalle';
      } else {
        title = 'Crear campaña de call center';
      }
      return title;
    },
    checkValidate() {
      this.isInvalid = false;
      if (!this.editMode) return null;
      if (this.form.max_attempts_new) {
        this.isInvalid = true;
        return 'numeric';
      }
      return null;
    },
  },
  methods: {
    formatGroupsForApi() {
      // eslint-disable-next-line no-unused-vars
      return this.form.groups.map(({ _localId, _version, ...group }) => group);
    },
    handleFileUpload(event) {
      this.form.file = event.target.files[0];
    },
    showUploadModal() {
      this.showCreate = true;
    },
    closeDialog() {
      this.$emit('close');
      setTimeout(() => {
        this.activeTab = 0;
        this.form = {
          ...this.emptyForm,
        };
      }, 200);
    },
    groupsHaveChanged() {
      return this.originalGroupsSnapshot !== JSON.stringify(this.formatGroupsForApi());
    },
    executeSave() {
      if (this.form.priority < 1) this.$set(this.form, 'priority', 1);
      const method = this.editMode ? 'patch' : 'post';
      const route = this.editMode ? `calls_lists/${this.callList.id}` : 'calls_lists';
      this.axios[method](route, {
        ...this.form,
        filters: JSON.stringify(this.form.filters),
        groups: this.formatGroupsForApi(),
      })
        .then((response) => {
          this.$emit('save', response.data);
          this.$emit('update', response.data);
          this.closeDialog();
        })
        .catch((e) => { console.log({ error: e }); });
    },
    confirmGroupsWarning() {
      this.showGroupsWarning = false;
      this.executeSave();
    },
    save() {
      if ([8, 9].includes(this.form.type_id)) {
        const hasNewFile = this.form.file && typeof this.form.file === 'object';
        if (!this.editMode && !hasNewFile) {
          EventBus.$emit('modal', 'Debe seleccionar un archivo Excel');
          return;
        }
        const formData = new FormData();
        const keys = Object.keys(this.form);
        for (let i = 0; i < keys.length; i += 1) {
          if (keys[i] === 'companyLoads') {
            const loads = this.form.companyLoads;
            if (Array.isArray(loads) && loads.length) {
              loads.forEach((id) => {
                formData.append('companyLoads[]', id);
              });
            }
          } else if (keys[i] === 'groups' || keys[i] === 'filters' || keys[i] === 'file') {
            // handled separately below
          } else if (this.form[keys[i]] != null) {
            formData.append(keys[i], this.form[keys[i]]);
          }
        }
        formData.append('filters', JSON.stringify(this.form.filters));
        formData.append('avoid_last_kpi', 0);
        formData.append('commitment_days_off', 0);
        this.formatGroupsForApi().forEach((group, index) => {
          Object.entries(group).forEach(([key, value]) => {
            let serialized = '';
            if (value != null && typeof value === 'object') {
              serialized = JSON.stringify(value);
            } else if (value != null) {
              serialized = value;
            }
            formData.append(`groups[${index}][${key}]`, serialized);
          });
        });
        if (!this.form.max_attempts_new) {
          formData.append('max_attempts_new', 0);
        }
        if (hasNewFile) {
          formData.append('file', this.form.file);
        }
        const route = this.editMode ? `calls_lists/${this.callList.id}` : 'calls_lists';
        // Laravel method spoof: multipart PATCH often not parsed by PHP
        if (this.editMode) {
          formData.append('_method', 'PATCH');
        }
        this.axios.post(route, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
          .then((response) => {
            this.$emit('save', response.data);
            this.$emit('update', response.data);
            this.closeDialog();
          })
          .catch((e) => {
            EventBus.$emit('modal', e.response.data);
          });
      } else {
        this.$validator.validateAll('callList').then((validation) => {
          if (!validation) {
            this.activeTab = 0;
            return;
          }
          if (this.editMode && this.groupsHaveChanged()) {
            this.showGroupsWarning = true;
            return;
          }
          this.executeSave();
        });
      }
    },
  },
  watch: {
    show() {
      if (this.show && this.editMode) {
        this.form = _.cloneDeep(
          _.pick(this.callList, Object.keys(this.emptyForm)),
        );
        this.form.filters = JSON.parse(this.callList.filters);
        let rawGroups = [];
        if (this.callList.groups) {
          rawGroups = typeof this.callList.groups === 'string'
            ? JSON.parse(this.callList.groups)
            : this.callList.groups;
        }
        this.originalGroupsSnapshot = JSON.stringify(rawGroups);
        this.form.groups = rawGroups.map(g => ({ ...g, _localId: uuidv1() }));
        // this.emailTemplateForm.company_email_id = this.form.company_email_id;
        // this.emailTemplateForm.subject = this.form.email_subject;
        // this.emailTemplateForm.text = this.form.email_text;
        const firstChannel = _.find(JSON.parse(this.callList.order), 'avoid_last_kpi') || {};
        this.$set(
          this.form,
          'avoid_last_kpi',
          firstChannel.avoid_last_kpi || false,
        );
        const lawConfig = _.find(JSON.parse(this.callList.order), 'keep_law_config') || {};
        this.$set(
          this.form,
          'keep_law_config',
          lawConfig.keep_law_config || true,
        );

        const loads = this.callList.company_loads.map(data => data.id);
        this.$set(
          this.form,
          'companyLoads',
          loads,
        );
      } else if (this.show) {
        this.$set(this.form, 'priority', this.callsListsPriority);
      }
      this.filtersListKey = uuidv1();
    },
    // emailTemplateForm: {
    //   deep: true,
    //   handler() {
    //     if (this.emailTemplateForm.company_email_id !== this.form.company_email_id) {
    //       this.form.company_email_id = this.emailTemplateForm.company_email_id;
    //     }
    //     if (this.emailTemplateForm.subject !== this.form.email_subject) {
    //       this.form.email_subject = this.emailTemplateForm.subject;
    //     }
    //     if (this.emailTemplateForm.text !== this.form.email_text) {
    //       this.form.email_text = this.emailTemplateForm.text;
    //     }
    //   },
    // },
  },
};
</script>

<style scoped>
table {
  width: 100% !important;
}
.input-file {
  padding-top: 24px;
  padding-left: 24px;
}
.message {
  font-size: 12px !important;
  color: #939393;
}
.attempt-container div {
  height: 55px !important;
}

/* Tabs */
.campaign-tabs >>> .v-tabs__bar {
  background-color: #fafafa;
  border-bottom: 2px solid #eeeeee;
  border-radius: 4px 4px 0 0;
}
.campaign-tabs >>> .v-tabs__slider {
  background: linear-gradient(to right, #f06c25, #f4a200) !important;
  height: 3px;
}
.campaign-tab {
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.3px;
  text-transform: none !important;
  color: #757575 !important;
  min-width: 0;
}
.campaign-tabs >>> .v-tabs__item--active {
  color: #f06c25 !important;
}
.campaign-tabs >>> .v-tabs__item--active .v-icon {
  color: #f06c25 !important;
}
.groups-warning-text {
  font-size: 14px;
  line-height: 1.6;
  color: #424242;
}
.tab-error-icon {
  color: #e53935 !important;
  animation: tab-error-pulse 1.8s ease-in-out infinite;
}
@keyframes tab-error-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
.tab-content {
  min-height: 360px;
  padding-top: 20px;
}
</style>



// WEBPACK FOOTER //
// src/components/CallList/Modal/Index.vue